import { IIntakeIdWithDates, IStudentRow, ErrorResponseBody } from 'types';
import { getAllStudents, IGetAllParams } from '../../../api/api';
import { AuthState } from '../../../store/auth/types';
import { studentToRow } from '../../../store/dashboard/helpers';
import { createGetAllStudentsParams } from '../../../store/dashboard/sagas/handleGetAllStudents';
import { ITotal } from '../../../store/dashboard/types';
import { IConfigState } from '../../../types';
import { EXPORT_PAGE_SIZE } from '../../../utilities/constants';

// Type guard to check if response is an error
function isErrorResponse(response: any): response is ErrorResponseBody {
  return response && typeof response === 'object' && 'error' in response;
}

const BATCH_SIZE = 5;

export const getAllStudentForExport = async (
  idToken: any,
  auth: AuthState,
  config: IConfigState,
  intakeId: string,
  headerTab: any,
  intakeRange: IIntakeIdWithDates | undefined,
  stages: string[],
  totals: ITotal[],
) => {
  const stagesOrder = stages.reduce(
    (acc, stage, idx) => {
      acc[stage] = idx;
      return acc;
    },
    {} as Record<string, number>,
  );

  const promises = totals
    .sort((a, b) => stagesOrder[a.casStatus] - stagesOrder[b.casStatus])
    .map(({ casStatus: stage, total }) => {
      const pages = Math.ceil(total / EXPORT_PAGE_SIZE);

      return Array(pages)
        .fill(0)
        .map((_, i) => {
          const params: IGetAllParams | undefined = createGetAllStudentsParams({
            auth,
            config,
            headerTab,
            idToken,
            intakeId,
            intakeRange,
          });
          if (!params) {
            console.error('params is undefined, will not export');
            return () => Promise.reject({ students: [] });
          }
          params.page = i + 1;
          params.stage = stage;
          return () => getAllStudents(params, true);
        });
    })
    .flat();

  const numBatches = Math.ceil(promises.length / BATCH_SIZE);
  const allStudents: IStudentRow[] = [];

  for (let index = 0; index < numBatches; index++) {
    const batch = promises.slice(index * BATCH_SIZE, (index + 1) * BATCH_SIZE);

    const batchResults = await Promise.allSettled(batch.map(f => f())).then(results => {
      return results
        .map(res => {
          if (res.status === 'fulfilled' && !isErrorResponse(res.value)) {
            return res.value.students;
          }
          return [];
        })
        .flat();
    });

    Array.prototype.push.apply(allStudents, batchResults);
  }

  return allStudents
    .map(student => {
      const row = studentToRow(student, auth, config);
      // Since we're exporting from the students endpoint, these should all be IStudentRow
      // Filter out any applicant rows (which have applicationId)
      if ('applicationId' in row) {
        console.warn('Unexpected applicant row in student export', row);
        return null;
      }
      return row as IStudentRow;
    })
    .filter((row): row is IStudentRow => row !== null);
};
